// Document Intelligence Pipeline — Polished UI
// Additions: summary strip, file drop-zone placeholder, editable extracted fields,
// bulk processing indicator, trend chart for statements, export.
import React, { useState, useEffect } from 'react';
import {
    Box, Button, Typography, Chip, Stack, Paper, Grid, Divider, TextField,
    IconButton, Tooltip,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import DownloadIcon from '@mui/icons-material/Download';
import EditIcon from '@mui/icons-material/Edit';
import AgentPageShell from './AgentPageShell';
import {
    SummaryGrid, SectionHeader, AIAnalysisBox, DrillTable, EmptyState,
    LoadingBlock, TrendChart, SimpleBarChart, DonutChart,
    fmtMoney, CHART_COLORS,
} from './AgentUIKit';
import {
    docParseInvoice, docContractTerms, docParseStatement,
    docContractSearch, docStatementTrends, docBankFees,
    docIntelligenceSummary,
} from './api';

const TABS = ['Invoice Parser', 'Contract Analysis', 'Bank Statement', 'Contract Search', 'Statement Trends', 'Fee Analysis'];

function exportJson(data, filename) {
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
}

function FileDropZone({ onFileText }) {
    const handleFile = async (file) => {
        if (!file) return;
        if (file.type.startsWith('text/') || file.name.match(/\.(txt|csv|json)$/i)) {
            const text = await file.text();
            onFileText(text);
        } else {
            onFileText(`[Binary file: ${file.name}, ${(file.size / 1024).toFixed(1)} KB]\n[For image/PDF upload, use the backend Gemini Vision OCR endpoint. Paste text below.]`);
        }
    };
    return (
        <Paper
            variant="outlined"
            sx={{ p: 2, textAlign: 'center', borderStyle: 'dashed', cursor: 'pointer', bgcolor: 'action.hover' }}
            onDragOver={e => { e.preventDefault(); }}
            onDrop={e => { e.preventDefault(); handleFile(e.dataTransfer.files?.[0]); }}
            onClick={() => document.getElementById('file-input-doc-intel')?.click()}
        >
            <input id="file-input-doc-intel" type="file" hidden
                onChange={e => handleFile(e.target.files?.[0])}
                accept=".txt,.csv,.json,.pdf,image/*" />
            <UploadFileIcon sx={{ fontSize: 32, color: 'text.secondary' }} />
            <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                Drop file here or click to select (text preferred). For PDFs/images, Gemini Vision OCR runs server-side.
            </Typography>
        </Paper>
    );
}

export default function DocumentIntelligenceAgent() {
    const [tab, setTab] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [summary, setSummary] = useState(null);

    const [invoiceText, setInvoiceText] = useState('');
    const [invoiceResult, setInvoiceResult] = useState(null);
    const [contractText, setContractText] = useState('');
    const [contractResult, setContractResult] = useState(null);
    const [statementText, setStatementText] = useState('');
    const [statementResult, setStatementResult] = useState(null);
    const [searchKeyword, setSearchKeyword] = useState('');
    const [searchResults, setSearchResults] = useState(null);
    const [trendsData, setTrendsData] = useState('');
    const [trendsResult, setTrendsResult] = useState(null);
    const [feeText, setFeeText] = useState('');
    const [feeResult, setFeeResult] = useState(null);

    const run = async (fn) => {
        setLoading(true); setError(null);
        try { return await fn(); }
        catch (e) { setError(e?.response?.data?.message || e.message); }
        finally { setLoading(false); }
    };

    useEffect(() => {
        docIntelligenceSummary().then(r => r?.data?.success && setSummary(r.data.data)).catch(() => {});
    }, []);

    const summaryCards = [
        { label: 'Invoices Parsed', value: summary?.docs?.invoices_parsed ?? '—', accent: 'primary' },
        { label: 'Contracts Analyzed', value: summary?.docs?.contracts_analyzed ?? '—', accent: 'info' },
        { label: 'Statements Processed', value: summary?.docs?.statements_processed ?? '—', accent: 'secondary' },
        { label: 'Avg Extraction Confidence', value: summary?.docs?.avg_confidence != null ? `${(summary.docs.avg_confidence * 100).toFixed(0)}%` : '—', accent: 'success' },
    ];

    const renderTab = () => {
        switch (tab) {
            case 0:
                return (
                    <Stack spacing={1.5}>
                        <SectionHeader title="Invoice Parser" subtitle="Extract fields from invoice text via Gemini" />
                        <FileDropZone onFileText={setInvoiceText} />
                        <TextField label="Invoice text" multiline rows={6} fullWidth size="small"
                            value={invoiceText} onChange={e => setInvoiceText(e.target.value)} />
                        <Stack direction="row" spacing={1}>
                            <Button variant="contained" size="small" disabled={!invoiceText.trim() || loading} startIcon={<PlayArrowIcon />}
                                onClick={async () => {
                                    const r = await run(() => docParseInvoice(invoiceText));
                                    if (r?.data?.success) setInvoiceResult(r.data.data);
                                }}>Parse</Button>
                        </Stack>
                        {invoiceResult && (
                            <Paper variant="outlined" sx={{ p: 2 }}>
                                <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
                                    <Typography variant="caption" fontWeight={600}>Extracted Fields (editable)</Typography>
                                    <Tooltip title="Export JSON"><IconButton size="small" onClick={() => exportJson(invoiceResult, 'invoice_parsed.json')}><DownloadIcon fontSize="small" /></IconButton></Tooltip>
                                </Stack>
                                <Grid container spacing={1}>
                                    <Grid item xs={6} md={3}>
                                        <TextField size="small" label="Vendor" value={invoiceResult.vendor || ''} onChange={e => setInvoiceResult({ ...invoiceResult, vendor: e.target.value })} fullWidth InputProps={{ endAdornment: <EditIcon sx={{ fontSize: 14, color: 'text.secondary' }} /> }} />
                                    </Grid>
                                    <Grid item xs={6} md={3}>
                                        <TextField size="small" label="Invoice #" value={invoiceResult.invoice_number || ''} onChange={e => setInvoiceResult({ ...invoiceResult, invoice_number: e.target.value })} fullWidth />
                                    </Grid>
                                    <Grid item xs={6} md={3}>
                                        <TextField size="small" label="Date" value={invoiceResult.date || ''} onChange={e => setInvoiceResult({ ...invoiceResult, date: e.target.value })} fullWidth />
                                    </Grid>
                                    <Grid item xs={6} md={3}>
                                        <TextField size="small" label="Total" value={invoiceResult.total || ''} onChange={e => setInvoiceResult({ ...invoiceResult, total: parseFloat(e.target.value) || 0 })} fullWidth />
                                    </Grid>
                                </Grid>
                                {invoiceResult.line_items?.length > 0 && (
                                    <Box sx={{ mt: 1 }}>
                                        <Typography variant="caption" fontWeight={600}>Line Items</Typography>
                                        <DrillTable
                                            columns={[
                                                { key: 'description', label: 'Description' },
                                                { key: 'quantity', label: 'Qty', align: 'right' },
                                                { key: 'unit_price', label: 'Unit', align: 'right', render: r => r.unit_price != null ? fmtMoney(r.unit_price) : '—' },
                                                { key: 'amount', label: 'Amount', align: 'right', render: r => fmtMoney(r.amount) },
                                            ]}
                                            rows={invoiceResult.line_items}
                                        />
                                    </Box>
                                )}
                                <AIAnalysisBox text={invoiceResult.ai_analysis} title="AI Invoice Analysis" />
                            </Paper>
                        )}
                    </Stack>
                );
            case 1:
                return (
                    <Stack spacing={1.5}>
                        <SectionHeader title="Contract Analysis" subtitle="Extract payment terms, penalties, renewal dates" />
                        <FileDropZone onFileText={setContractText} />
                        <TextField label="Contract text" multiline rows={6} fullWidth size="small"
                            value={contractText} onChange={e => setContractText(e.target.value)} />
                        <Button variant="contained" size="small" disabled={!contractText.trim() || loading} startIcon={<PlayArrowIcon />} sx={{ alignSelf: 'flex-start' }}
                            onClick={async () => {
                                const r = await run(() => docContractTerms(contractText));
                                if (r?.data?.success) setContractResult(r.data.data);
                            }}>Extract Terms</Button>
                        {contractResult && (
                            <Paper variant="outlined" sx={{ p: 2 }}>
                                <Grid container spacing={1}>
                                    {[
                                        { label: 'Financial Terms', items: contractResult.financial_terms || [], color: CHART_COLORS.primary },
                                        { label: 'Payment Terms', items: contractResult.payment_terms || [], color: CHART_COLORS.secondary },
                                        { label: 'Penalties', items: contractResult.penalties || [], color: CHART_COLORS.danger },
                                        { label: 'Renewal Dates', items: contractResult.renewal_dates || [], color: CHART_COLORS.warning },
                                    ].map((section, i) => (
                                        <Grid key={i} item xs={12} md={6}>
                                            <Paper variant="outlined" sx={{ p: 1.5, borderLeft: '3px solid', borderColor: section.color }}>
                                                <Typography variant="caption" fontWeight={600}>{section.label}</Typography>
                                                <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                                                    {section.items.length > 0 ? section.items.map((t, idx) => (
                                                        <Typography key={idx} variant="body2">
                                                            • {typeof t === 'string' ? t : (t.term ? `${t.term}: ${t.value}` : (t.description || t.date))}
                                                        </Typography>
                                                    )) : <Typography variant="body2" color="text.secondary">None extracted</Typography>}
                                                </Stack>
                                            </Paper>
                                        </Grid>
                                    ))}
                                </Grid>
                                <AIAnalysisBox text={contractResult.ai_analysis} title="AI Contract Risk Assessment" />
                            </Paper>
                        )}
                    </Stack>
                );
            case 2:
                return (
                    <Stack spacing={1.5}>
                        <SectionHeader title="Bank Statement Parser" subtitle="Extract transactions from bank statement PDF/text" />
                        <FileDropZone onFileText={setStatementText} />
                        <TextField label="Statement text" multiline rows={6} fullWidth size="small"
                            value={statementText} onChange={e => setStatementText(e.target.value)} />
                        <Button variant="contained" size="small" disabled={!statementText.trim() || loading} startIcon={<PlayArrowIcon />} sx={{ alignSelf: 'flex-start' }}
                            onClick={async () => {
                                const r = await run(() => docParseStatement(statementText));
                                if (r?.data?.success) setStatementResult(r.data.data);
                            }}>Parse Statement</Button>
                        {statementResult && (
                            <Paper variant="outlined" sx={{ p: 2 }}>
                                <SummaryGrid cards={[
                                    { label: 'Period', value: statementResult.period || '—', accent: 'info' },
                                    { label: 'Opening Balance', value: fmtMoney(statementResult.opening_balance), accent: 'primary' },
                                    { label: 'Closing Balance', value: fmtMoney(statementResult.closing_balance), accent: 'primary' },
                                    { label: 'Transactions', value: statementResult.transactions?.length || 0, accent: 'secondary' },
                                ]} />
                                {statementResult.transactions?.length > 0 && (
                                    <DrillTable
                                        columns={[
                                            { key: 'date', label: 'Date' },
                                            { key: 'description', label: 'Description' },
                                            { key: 'debit', label: 'DR', align: 'right', render: r => r.debit ? fmtMoney(r.debit) : '—' },
                                            { key: 'credit', label: 'CR', align: 'right', render: r => r.credit ? fmtMoney(r.credit) : '—' },
                                            { key: 'balance', label: 'Balance', align: 'right', render: r => r.balance != null ? fmtMoney(r.balance) : '—' },
                                        ]}
                                        rows={statementResult.transactions}
                                        maxHeight={420}
                                    />
                                )}
                                <AIAnalysisBox text={statementResult.ai_analysis} title="AI Statement Analysis" />
                            </Paper>
                        )}
                    </Stack>
                );
            case 3:
                return (
                    <Stack spacing={1.5}>
                        <SectionHeader title="Contract Repository Search" subtitle="Search archived contracts by keyword" />
                        <Stack direction="row" spacing={1}>
                            <TextField label="Keyword" size="small" fullWidth
                                value={searchKeyword} onChange={e => setSearchKeyword(e.target.value)} />
                            <Button variant="contained" size="small" disabled={!searchKeyword.trim() || loading} startIcon={<PlayArrowIcon />}
                                onClick={async () => {
                                    const r = await run(() => docContractSearch(searchKeyword));
                                    if (r?.data?.success) setSearchResults(r.data.data);
                                }}>Search</Button>
                        </Stack>
                        {searchResults && (
                            <>
                                <Typography variant="caption">{searchResults.total || searchResults.results?.length || 0} results</Typography>
                                <DrillTable
                                    columns={[
                                        { key: 'title', label: 'Title', render: r => r.title || r.name || '—' },
                                        { key: 'category', label: 'Category' },
                                        { key: 'relevance_score', label: 'Relevance', align: 'right', render: r => (
                                            <Chip size="small" label={r.relevance_score != null ? r.relevance_score.toFixed(2) : '—'}
                                                color={r.relevance_score >= 0.8 ? 'success' : r.relevance_score >= 0.5 ? 'warning' : 'default'} />
                                        )},
                                        { key: 'value', label: 'Value', align: 'right', render: r => r.value != null ? fmtMoney(r.value) : '—' },
                                    ]}
                                    rows={searchResults.results || searchResults.items || []}
                                />
                            </>
                        )}
                    </Stack>
                );
            case 4:
                return (
                    <Stack spacing={1.5}>
                        <SectionHeader title="Statement Trends Analyzer" subtitle="Analyze multi-month bank statement data" />
                        <TextField
                            label="Statements data (JSON)"
                            placeholder='[{"month": "2024-01", "income": 50000, "expenses": 35000}, ...]'
                            multiline rows={5} fullWidth size="small"
                            value={trendsData} onChange={e => setTrendsData(e.target.value)}
                        />
                        <Button variant="contained" size="small" disabled={!trendsData.trim() || loading} startIcon={<PlayArrowIcon />} sx={{ alignSelf: 'flex-start' }}
                            onClick={async () => {
                                let parsed;
                                try { parsed = JSON.parse(trendsData); }
                                catch { setError('Invalid JSON'); return; }
                                const r = await run(() => docStatementTrends(parsed));
                                if (r?.data?.success) setTrendsResult(r.data.data);
                            }}>Analyze</Button>
                        {trendsResult && (
                            <Paper variant="outlined" sx={{ p: 2 }}>
                                {(trendsResult.monthly_summaries || []).length > 0 && (
                                    <>
                                        <TrendChart
                                            data={trendsResult.monthly_summaries.map(m => ({
                                                month: m.month || m.period,
                                                income: m.income,
                                                expenses: m.expenses,
                                                net: m.net,
                                            }))}
                                            xKey="month"
                                            series={[
                                                { key: 'income', label: 'Income', color: CHART_COLORS.success },
                                                { key: 'expenses', label: 'Expenses', color: CHART_COLORS.danger },
                                                { key: 'net', label: 'Net', color: CHART_COLORS.primary },
                                            ]}
                                            height={280}
                                            yFormatter={v => fmtMoney(v)}
                                        />
                                        <DrillTable
                                            columns={[
                                                { key: 'month', label: 'Month', render: r => r.month || r.period },
                                                { key: 'income', label: 'Income', align: 'right', render: r => fmtMoney(r.income) },
                                                { key: 'expenses', label: 'Expenses', align: 'right', render: r => fmtMoney(r.expenses) },
                                                { key: 'net', label: 'Net', align: 'right', render: r => <b>{fmtMoney(r.net)}</b> },
                                                { key: 'mom_change', label: 'MoM', align: 'right', render: r => r.mom_change != null
                                                    ? <Chip size="small" color={r.mom_change >= 0 ? 'success' : 'error'} label={`${r.mom_change >= 0 ? '+' : ''}${r.mom_change.toFixed(1)}%`} />
                                                    : '—' },
                                            ]}
                                            rows={trendsResult.monthly_summaries}
                                        />
                                    </>
                                )}
                                <AIAnalysisBox text={trendsResult.narrative || trendsResult.ai_analysis} title="AI Trend Narrative" />
                            </Paper>
                        )}
                    </Stack>
                );
            case 5:
                return (
                    <Stack spacing={1.5}>
                        <SectionHeader title="Bank Fee Analyzer" subtitle="Detect and categorize bank fees from statement transactions" />
                        <TextField
                            label="Statement transactions text"
                            multiline rows={5} fullWidth size="small"
                            value={feeText} onChange={e => setFeeText(e.target.value)}
                        />
                        <Button variant="contained" size="small" disabled={!feeText.trim() || loading} startIcon={<PlayArrowIcon />} sx={{ alignSelf: 'flex-start' }}
                            onClick={async () => {
                                const r = await run(() => docBankFees(feeText));
                                if (r?.data?.success) setFeeResult(r.data.data);
                            }}>Detect Fees</Button>
                        {feeResult && (
                            <Paper variant="outlined" sx={{ p: 2 }}>
                                {feeResult.total_fees != null && (
                                    <SummaryGrid cards={[
                                        { label: 'Total Fees Detected', value: fmtMoney(feeResult.total_fees), accent: 'danger' },
                                        { label: 'Categories', value: (feeResult.categories || []).length, accent: 'warning' },
                                    ]} cols={2} />
                                )}
                                {(feeResult.categories || []).length > 0 && (
                                    <Box sx={{ mt: 1 }}>
                                        <DonutChart
                                            data={feeResult.categories.map(c => ({ name: c.name || c.category, value: c.amount || c.total || 0 }))}
                                            height={240}
                                            formatter={v => fmtMoney(v)}
                                        />
                                    </Box>
                                )}
                                <AIAnalysisBox text={feeResult.recommendations || feeResult.ai_analysis} title="AI Fee Reduction Recommendations" />
                            </Paper>
                        )}
                    </Stack>
                );
            default: return null;
        }
    };

    return (
        <AgentPageShell
            title="Document Intelligence Pipeline"
            subtitle="Gemini-powered document processing — invoices, contracts, bank statements. File drop-zone + field editor + bulk analysis."
            tabs={TABS}
            activeTab={tab}
            onTabChange={setTab}
            error={error}
        >
            <Stack spacing={1.5}>
                <SummaryGrid cards={summaryCards} />
                <Divider />
                {renderTab()}
            </Stack>
        </AgentPageShell>
    );
}
